import { Typography, InputBase, IconButton, Grid, Box, Button, Chip, Tooltip } from '@mui/material';
import { SearchOutlined, Add, Edit, Delete } from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddorUpdateAppointment from './AddorUpdateAppointment';
import { useEffect, useState, useRef, useMemo } from 'react';
import { deleteAppointmentMapping, getAllAppointmentMappings, GetAllCerboTags, GetAllCRMTags, GetAllCustomFields, GetAppointmentTypeEntities, GetProviderList } from '../../Service/AxiosService';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';


const AppointmentList = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [customFields, setCustomFields] = useState([{ fieldId: '', fieldName: '' }]);
  const [cerboTags, setCerboTags] = useState<any[]>([]);
  const [crmTags, setCrmTags] = useState<any[]>([]);
  const [rows, setRows] = useState([] as any[]);
  const [deleteRow, setDeleteRow] = useState<any | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const [appointmentData, setAppointmentData] = useState<any[]>([]);
  const [providers, setProviders] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Dynamic page size logic (moved inside component)
  const dataGridWrapperRef = useRef<HTMLDivElement>(null);
  const ROW_HEIGHT = 32;
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });

  const calculatePageSize = (gridEl: HTMLDivElement) => {
    const header = gridEl.querySelector('.MuiDataGrid-columnHeaders');
    const footer = gridEl.querySelector('.MuiDataGrid-footerContainer');
    const headerHeight = header?.getBoundingClientRect().height ?? 0;
    const footerHeight = footer?.getBoundingClientRect().height ?? 0;
    const gridHeight = gridEl.getBoundingClientRect().height;
    const availableHeight = gridHeight - headerHeight - footerHeight;
    const rowsThatFit = Math.floor(availableHeight / ROW_HEIGHT);
    return Math.max(5, rowsThatFit);
  };

  useEffect(() => {
    document.title = `${process.env.REACT_APP_CLINIC_NAME || 'Clinic'} - Appointments`;

    if (!dataGridWrapperRef.current) return;
    const observer = new ResizeObserver(() => {
      const newPageSize = calculatePageSize(dataGridWrapperRef.current!);
      setPaginationModel(prev => {
        if (prev.pageSize === newPageSize) return prev;
        return { ...prev, pageSize: newPageSize, page: 0 };
      });
    });
    observer.observe(dataGridWrapperRef.current);
    return () => observer.disconnect();
  }, []);
  const pageSizeOptions = useMemo(() => [5, 10, 20, 50, paginationModel.pageSize].filter((v, i, arr) => arr.indexOf(v) === i).sort((a, b) => a - b), [paginationModel.pageSize]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter((row) =>
      row.AppointmentTitle?.toLowerCase().includes(term) ||
      row.Providers?.toLowerCase().includes(term) ||
      row.CustomField?.toLowerCase().includes(term) ||
      (Array.isArray(row.RequiredAcTags) && row.RequiredAcTags.some((t: string) => t.toLowerCase().includes(term))) ||
      (Array.isArray(row.RequiredCerboTags) && row.RequiredCerboTags.some((t: string) => t.toLowerCase().includes(term)))
    );
  }, [rows, searchTerm]);

  useEffect(() => {
    fetchAppointments();
  }, []);

  const handleEdit = async (srow: any) => {
    setSelectedRow(srow);
    setOpenDialog(true);
  };

  const onCloseDialog = async () => {
    setOpenDialog(false);
    setSelectedRow(null);
    await fetchAppointments();
  };

  const handleDelete = async (row: any) => {
    setDeleteRow(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteRow && deleteRow.id) {
      const res = await deleteAppointmentMapping(deleteRow.id);
      if (res.success) {
        await fetchAppointments(); // Refresh the list after successful delete
      }
    }
    setDeleteDialogOpen(false);
    setDeleteRow(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteRow(null);
  };

  const columns: GridColDef[] = [
    { field: 'S_no', headerName: 'S.No', flex: 0.5, headerAlign: 'center', align: 'center' },
    { field: 'AppointmentTitle', headerName: 'Appointment Title', flex: 1.5, headerAlign: 'left', align: 'left' },
    { field: 'Providers', headerName: 'Providers', flex: 1.5, headerAlign: 'left', align: 'left' },
    { field: 'CustomField', headerName: 'Custom Field', flex: 1.5, headerAlign: 'left', align: 'left' 
      , renderCell: (params) => {
        const fieldString = Array.isArray(params.value) ? params.value.join(', ') : (params.value || '');
        if (!fieldString) return null;
        return (
          <Tooltip title={fieldString} placement="top" arrow>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', maxWidth: 210 }}>
              {fieldString}
            </span>
          </Tooltip>
        );
      }
    },
    { field: 'HasTagCondition', headerName: 'Enable Condition', flex: 1.5, headerAlign: 'center', align: 'center',
      renderCell: (params) => {
              const isAdd = params.value === 'Yes';
              return (
                <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <Box
                    sx={{
                      height: 24,
                      px: 1.5,
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      display: 'flex',              // 👈 change here
                      alignItems: 'center',         // 👈 vertical center
                      justifyContent: 'center',     // 👈 horizontal center
                      color: isAdd ? '#15803d' : '#b91c1c',
                      backgroundColor: isAdd ? '#dcfce7' : '#fee2e2',
                    }}
                  >
                    {params.value}
                  </Box>
                </Box>
              );
            },
    },
    {
      field: 'RequiredAcTags',
      headerName: 'Required HL Tags',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const tagString = Array.isArray(params.value) ? params.value.join(', ') : (params.value || '');
        if (!tagString) return null;
        return (
          <Tooltip title={tagString} placement="top" arrow>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
              {tagString}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'RequiredCerboTags',
      headerName: 'Required Cerbo Tags',
      flex: 1,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => {
        const tagString = Array.isArray(params.value) ? params.value.join(', ') : (params.value || '');
        if (!tagString) return null;
        return (
          <Tooltip title={tagString} placement="top" arrow>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block', width: '100%' }}>
              {tagString}
            </span>
          </Tooltip>
        );
      },
    },
    { field: 'IsStartOver', headerName: 'Enable Start Over', flex: 1.5, headerAlign: 'center', align: 'center' },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      minWidth: 100,
      maxWidth: 100,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      filterable: false,
      disableReorder: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton
            aria-label="edit"
            color="primary"
            size="small"
            onClick={() => handleEdit(params.row)}
          >
            <Edit sx={{fontSize: 15}} />
          </IconButton>
          <IconButton
            aria-label="delete"
            color="error"
            size="small"
            onClick={() => handleDelete(params.row)}
          >
            <Delete sx={{fontSize: 15}} />
          </IconButton>
        </Box>
      ),
    },
  ];

  const fetchAppointments = async () => {
      let csField: any = [];
      const csFields = await GetAllCustomFields();
      // Normalize custom fields response to an array to avoid runtime errors
      const rawCs = csFields?.data ?? csFields;
      const csData: any[] = Array.isArray(rawCs)
        ? rawCs
        : Array.isArray(rawCs?.data)
        ? rawCs.data
        : [];

      if (csData.length > 0) {
        csField = csData.map((field: { fieldId: string; fieldName: string }) => ({
          fieldId: field.fieldId,
          fieldName: field.fieldName,
        }));
        setCustomFields(csField);
      }

      let cerboTagList: any = [];
      const cerboTags = await GetAllCerboTags();
      const rawCerbo = cerboTags?.data ?? cerboTags;
      const cerboData: any[] = Array.isArray(rawCerbo)
        ? rawCerbo
        : Array.isArray(rawCerbo?.data)
        ? rawCerbo.data
        : [];

      if (cerboData.length > 0) {
        cerboTagList = cerboData.map((tag: { id: string; name: string; tagCategory: string }) => ({
          tagId: tag.id,
          tagName: `${tag.tagCategory} - ${tag.name}`,
        }));
        setCerboTags(cerboTagList);
      }

      let appointments: any = [];
      const getAppointments = await GetAppointmentTypeEntities();
      const rawAppointments = getAppointments?.data ?? getAppointments;
      const appointmentsData: any[] = Array.isArray(rawAppointments)
        ? rawAppointments
        : Array.isArray(rawAppointments?.data)
        ? rawAppointments.data
        : [];

      if (appointmentsData.length > 0) {
        appointments = appointmentsData.map((appointment: { appointmentTypeId: string; appointmentTypeName: string }) => ({
          appointmentTypeId: appointment.appointmentTypeId,
          appointmentTypeName: appointment.appointmentTypeName,
        }));
        setAppointmentData(appointments);
      }

      let crmTagList: any = [];
      const crmTags = await GetAllCRMTags();
      const rawCrmTags = crmTags?.data ?? crmTags;
      const crmTagsData: any[] = Array.isArray(rawCrmTags)
        ? rawCrmTags
        : Array.isArray(rawCrmTags?.data)
        ? rawCrmTags.data
        : [];

      if (crmTagsData.length > 0) {
        crmTagList = crmTagsData.map((tag: { tagId: string; tagName: string }) => ({
          tagId: tag.tagId,
          tagName: tag.tagName,
        }));
        setCrmTags(crmTagList);
      } else {
        setCrmTags([]);
      }

      let providers: any = [];
      const getProviders = await GetProviderList();
      const rawProviders = getProviders?.data ?? getProviders;
      const providersData: any[] = Array.isArray(rawProviders)
        ? rawProviders
        : Array.isArray(rawProviders?.data)
        ? rawProviders.data
        : [];

      if (providersData.length > 0) {
        providers = providersData.map((provider: any) => ({
          providerId: provider.providerId,
          providerName: `${provider.firstName} ${provider.lastName}`,
        }));
        setProviders(providers);
      } else {
        setProviders([]);
      }

      const getAppointmentMappings = await getAllAppointmentMappings();
      const rawAppointmentMappings = getAppointmentMappings?.data ?? getAppointmentMappings;
      const appointmentMappingsData: any[] = Array.isArray(rawAppointmentMappings)
        ? rawAppointmentMappings
        : Array.isArray(rawAppointmentMappings?.data)
        ? rawAppointmentMappings.data
        : [];

      if (appointmentMappingsData.length > 0) {
        const appointmentData = appointmentMappingsData.map((appointment: any, index: number) => ({
          id: appointment.id,
          S_no: index + 1,
          AppointmentTitle: appointments.find((a: any) => a.appointmentTypeId === appointment.appointmentType)?.appointmentTypeName || '',
          HasTagCondition: appointment.hasTagCondition ? 'Yes' : 'No',
          RequiredAcTags: crmTagList
            .filter((a: any) => (appointment.requiredCRMTags?.split(',') || []).includes(a.tagId))
            .map((a: any) => a.tagName),
          RequiredCerboTags: cerboTagList
            .filter((tag: any) => (appointment.requiredCerboTags?.split(',') || []).includes(tag.tagId))
            .map((tag: any) => tag.tagName),
          IsStartOver: appointment.isStartOver ? 'Yes' : 'No',
          Providers: providers.find((a: any) => a.providerId === appointment.providerId)?.providerName || '',
          CustomField: appointment.customFieldJson
            ? JSON.parse(appointment.customFieldJson).flatMap((cf: any) => [
                cf.DateFieldId,
                cf.TypeFieldId,
                cf.LocationFieldId,
                cf.ProviderFieldId
              ]).filter((id: string) => id && id.trim() !== "")
              .map((id: string) =>
            csField.find((f: any) => String(f.fieldId) === String(id))?.fieldName
          )
      .filter(Boolean)
            : [],
          // Raw slot IDs (Date/Type/Location/Provider per slot) so the edit form can pre-select the
          // exact saved fields instead of guessing the slot from the field name.
          CustomFieldSlots: appointment.customFieldJson ? JSON.parse(appointment.customFieldJson) : [],
          enableProviderSelection: appointment.enableProvider,
          GhlCalendarId: appointment.ghlCalendarId || '',
          GhlAssignedUserId: appointment.ghlAssignedUserId || '',
        }));
        setRows(appointmentData);
      } else {
        setRows([]);
      }
    };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 0, m: 0, bgcolor: 'transparent', display: 'flex', flexDirection: 'column' }}>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 1, mt: 0.5 }}>
        <Grid size="auto">
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 17,
            }}
            variant="subtitle1"
          >
            Appointments
          </Typography>
        </Grid>
        <Grid>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: 300,
                bgcolor: '#fff',
                height: 36,
                border: '1px solid #dedede',
                borderRadius: 2,
              }}
            >
              <InputBase
                placeholder="Search appointments..."
                inputProps={{ 'aria-label': 'search appointments' }}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{
                  flex: 1,  
                  fontSize: 15,
                  bgcolor: 'transparent',
                  px: 2,
                  py: 0.5,
                  height: 32,
                }}
              />
              <IconButton sx={{ p: '4px', color: '#23244A', height: 32, width: 32 }} aria-label="search">
                <SearchOutlined fontSize="small" />
              </IconButton>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add fontSize="small" />}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                bgcolor: '#3D52A0',
                '&:hover': { bgcolor: '#31417f' },
                fontSize: 14,
                borderRadius: 2,
                minWidth: 120,
                py: 1,
                px: 2,
                height: 36,
              }}
              onClick={() => setOpenDialog(true)}
            >
              New Appointment Rule
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Box ref={dataGridWrapperRef}
        sx={{
          flex: 1,
          width: '100%',
          minHeight: 0,
          pb: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#fff',
          borderRadius: 3,
          boxShadow: '0 2px 16px rgba(24,26,42,0.08)',
        }}
      >
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pagination
          rowHeight={ROW_HEIGHT}
          pageSizeOptions={pageSizeOptions}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sx={{
            borderRadius: 3,
            bgcolor: '#fff',
            fontSize: 13,
            flex: 1,
            boxShadow: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: '#FAFAFC',
              fontWeight: 700,
              fontSize: 13,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
              }
            },
            '& .MuiDataGrid-cell': { fontSize: 12, py: 0, color: '#000' },
            '& .MuiDataGrid-row': { minHeight: 32, maxHeight: 32 },
            '& .MuiDataGrid-virtualScroller': { overflowX: 'auto !important' },
            '& .MuiTablePagination-root': { fontSize: 12, borderTop: '1px solid #e0e0e0' },
            '& .MuiDataGrid-footerContainer': {
              bgcolor: '#FAFAFC',
              borderBottomLeftRadius: 12,
              borderBottomRightRadius: 12,
            },
            '& .MuiDataGrid-selectedRowCount': { color: '#23244A' },
            '& .MuiDataGrid-cell[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                backgroundColor: '#fff',
                zIndex: 1,
              },

              '& .MuiDataGrid-columnHeader[data-field="actions"]': {
                position: 'sticky',
                right: 0,
                backgroundColor: '#FAFAFC',
                zIndex: 2,
              },
          }}
        />
      </Box>
      <AddorUpdateAppointment
        open={openDialog}
        onClose={() => onCloseDialog()}
        cerboTags={cerboTags}
        crmTags={crmTags}
        customFields={customFields}
        initialValues={selectedRow}
        appointmentData={appointmentData}
        providers={providers}
      />
      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: '#FAFAFC',
            boxShadow: '0 2px 16px rgba(24,26,42,0.10)',
            minWidth: 320,
          }
        }}
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ fontSize: 15, maxHeight: "80vh", overflowY: "auto" }}>
          Are you sure you want to delete this appointment mapping?
        </DialogContent>
        <DialogActions sx={{ py: 2, px: 3 }}>
          <Button onClick={handleCancelDelete} color="secondary" variant="outlined" size="small"
            sx={{
              borderRadius: 2,
              border: 'none',
              background: '#dedede',
              color: '#000',
              textTransform: 'capitalize',
              py: 1,
              px: 2,
              '&:hover': { background: '#d6d6d6' },
            }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained" size="small"
            sx={{
              borderRadius: 2,
              background: '#d32f2f',
              textTransform: 'capitalize',
              py: 1,
              px: 2,
              '&:hover': { background: '#b71c1c' }
            }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentList;